import { Component, ElementRef, inject, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';

import { EventDelegatorService } from '@services/event-delegator.service';
import { ChartLibraryService } from '@services/chart-library.service';
import { MenuService } from '@services/menu.service';
import { escape } from 'lodash';

declare const L;

@Component({
  selector: 'app-choropleth-map',
  standalone: true,
  imports: [],
  templateUrl: './choropleth-map.component.html',
  styleUrls: ['./choropleth-map.component.scss'],
})
export class ChoroplethMapComponent implements OnInit, OnDestroy {
  @ViewChild('map', { static: true }) mapElement: ElementRef;
  @Input() data;
  features: any[] = [];
  map: any;
  eventBus: any[] = [];
  menuService = inject(MenuService);
  /**
   * ChoroplethMap data is in the format of { 'mapData': { features: [] },'type': 'choropleth-map'}
   */
  constructor(
    private eventDelegatorService: EventDelegatorService,
    private chartLibraryService: ChartLibraryService
  ) { }

  ngOnInit(): void {
    this.init();
    this.showOrHidePngDownloadButton(true);
    this.eventBus.push(
      this.eventDelegatorService.on('leaflet-export-map', (data) => {
        this.chartLibraryService.exportLeafletImage(this.map, data.filename);
      })
    );

    this.eventBus.push(
      this.eventDelegatorService.on('ngx-event-analyze', (ev) => {
        if (ev.action === 'print-map') {
          const name = `${ev.data.name}_choropleth`;
          this.chartLibraryService.exportLeafletImage(this.map, name);
        }
      })
    );
  }

  showPngDownloadButton() {
    let menuConfigData = null;
    this.menuService.menuConfig$.subscribe((value) => {
      menuConfigData = value;
    });
    if (menuConfigData) {
      const exportPngObj = menuConfigData.rightActions.find((action) => action.key === 'export_png');
      exportPngObj.visible = true;
      this.menuService.updateMenuItem('export_png', exportPngObj);
    }
  }

  showOrHidePngDownloadButton(visible: boolean) {
    this.menuService.updateMenuItem('export_png', { visible });
  }

  ngOnDestroy(): void {
    this.eventBus.forEach((e) => {
      e?.unsubscribe();
    });
    this.showOrHidePngDownloadButton(false);
  }

  init() {
    if (this.map) {
      this.map.remove();
    }
    this.initMap(this.data);
  }

  initMap(data) {
    let features: any;
    // var map: any;
    let geojson;
    const unit = '';
    let analyticsData;
    if (data.mapData) {
      features = data.mapData.features || [];
      analyticsData = data.mapData.analytics;
    }

    this.map = L.map(this.mapElement.nativeElement, {
      maxBoundsViscosity: 1.0,
      zoomControl: true,
      scrollWheelZoom: true,
      touchZoom: true,
      preferCanvas: true,
      // doubleClickZoom: this.mapControls,
      boxZoom: true,
      attributionControl: false,
      keyboard: true,
    })
      .setView([15.395158, 19.062826], 2)
      .whenReady(() => {
        setTimeout(() => {
          this.map.invalidateSize();
        }, 100);
      });

    const options = {
      id: 'mapbox/light-v9',
      tileSize: 512,
      maxZoom: 18,
      zoomOffset: -1,
      minZoom: 1,
      continuousWorld: false,
      noWrap: true,
      bounds: [[-90, -180], [90, 180]]
    };
    const tiles = this.chartLibraryService.getMapTiles(L, options);
    tiles.addTo(this.map);

    // control that shows state info on hover
    const info = L.control({ position: 'topleft' });

    info.onAdd = function (map) {
      this._div = L.DomUtil.create('div', 'infolegend');
      this.update();
      return this._div;
    };

    info.update = function (props) {
      const value = props ? Number(props.value).toExponential(2) : '';
      const text = props ? `<b>${escape(props.name)}</b><br />${escape(value)} ${escape(unit)}` : 'click on a country';
      this._div.innerHTML = `<h4>Values</h4>${text}`;
    };
    info.addTo(this.map);

    function style(feature) {
      return {
        weight: 2,
        opacity: 1,
        color: 'white',
        dashArray: '3',
        fillOpacity: 0.7,
        fillColor: getColor(feature.properties.density),
      };
    }

    // color array
    function getColor(d) {
      return d > 3.5 ? '#800026'
        : d > 3 ? '#BD0026'
          : d > 2.5 ? '#E31A1C'
            : d > 2 ? '#FC4E2A'
              : d > 1.5 ? '#FD8D3C'
                : d > 1 ? '#FEB24C'
                  : d > 0.5 ? '#FED976'
                    : '#FFEDA0';
    }

    function highlightFeature(e) {
      const layer = e.target;
      layer.setStyle({
        weight: 5,
        color: '#666',
        dashArray: '',
        fillOpacity: 0.7,
      });
      if (!L.Browser.ie && !L.Browser.opera && !L.Browser.edge) {
        layer.bringToFront();
      }
      if (e.type !== 'mouseover') {
        info.update(layer.feature.properties);
      }
    }

    function resetHighlight(e) {
      geojson.resetStyle(e.target);
      info.update();
    }

    // function zoomToFeature(e) {
    //   this.map.fitBounds(e.target.getBounds());
    // }

    function onEachFeature(feature, layer) {
      layer.on({
        mouseover: highlightFeature,
        // mouseout: resetHighlight,
        click: highlightFeature,
      });
    }
    /* global statesData */
    geojson = L.geoJson(features, {
      style,
      onEachFeature,
    }).addTo(this.map);

    const legend = L.control({ position: 'bottomleft' });

    legend.onAdd = function (cmap) {
      const div = L.DomUtil.create('div', 'infolegend legend');
      const grades = [0, 0.5, 1, 1.5, 2, 2.5, 3, 3.5];
      const labels = [];
      // loop through our density intervals and generate a label with a colored square for each interval
      for (let i = 0; i < grades.length; i++) {
        const nextText = grades[i + 1] ? `&ndash;${grades[i + 1]}<br>` : '+';
        div.innerHTML += `<i style="background:${getColor(grades[i] + 1)}"></i> ${grades[i]}${nextText}`;
      }
      return div;
    };

    legend.addTo(this.map);

    // analytics data
    const analytics_legend = L.control({
      position: 'topleft',
    });
    if (analyticsData) {
      analytics_legend.onAdd = function (cmap) {
        this._div = L.DomUtil.create('div', 'analytics_legend'); // create a div with a class "info"
        this.update();
        return this._div;
      };
      let key; let
        value;
      for (const i in analyticsData) {
        key = i;
        value = analyticsData[i].toFixed(2);
      }
      // method that we will use to update the control based on feature properties passed
      analytics_legend.update = function (props) {
        this._div.innerHTML = `<b>Analytics</b><br/><h4>${escape(key)}=${value}</h4>`;
      };
      analytics_legend.addTo(this.map);
    }
  }

  redrowMap() {
    this.init();
  }
}
